/**
 * Unique art print generator.
 *
 * Workflow:
 *   1. Log in with Nostr — prints bind to your npub (pubkey).
 *   2. Batch-upload art images — one print is created per image, in
 *      file order.
 *   3. Each print mints a fresh verification keypair + naddr and encodes
 *      a claim URL ({origin}/{naddr}#verify={nsec}) into a branded QR.
 *   4. Print fronts (the art) and backs (the QR) duplex on US Letter,
 *      4-up at 4"x4".
 *
 * Each generation mints brand-new keypairs, so the JSON export (naddr +
 * nsec + claim URL per print) should be saved before navigating away —
 * it's needed to later publish the addressable events.
 *
 * Generalized + pop-art restyled from the treasures.to Treasure Trolls
 * print sheet.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import {
  Loader2,
  Printer,
  Copy,
  Download,
  Upload,
  Trash2,
  Image as ImageIcon,
  Sparkles,
  QrCode,
  MapPin,
  Trophy,
} from "lucide-react";
import { nip19 } from "nostr-tools";
import {
  generateVerificationKeyPair,
  printToNaddr,
  buildClaimUrl,
  generateCompactDTag,
  type VerificationKeyPair,
} from "@/lib/nostr";
import { generateClaimQR } from "@/lib/claim-qr";
import { loadChestPng, getCachedChestPng } from "@/lib/chest-logo";
import { DEFAULT_CONFIG, type PrintConfig } from "@/lib/config";
import { cn } from "@/lib/cn";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { LoginArea } from "@/components/auth/LoginArea";

/**
 * Fixed duplex calibration. Long-edge duplex on consumer printers tends
 * to land the back side a hair left and high relative to the front.
 * These offsets (inches) nudge the back sheet right/down into register.
 */
const BACK_OFFSET_X = 0.05;
const BACK_OFFSET_Y = 0;

/** Deep purple page backdrop (matches Treasure Trolls). */
const TROLL_PURPLE = "#3d1a5e";

/** Saturated pop-art palette for confetti, glows, and accents. */
const POP_COLORS = [
  "#ff3ea5", // hot pink
  "#ff7a1a", // orange
  "#ffd23f", // yellow
  "#3ddc97", // mint
  "#3ec1ff", // cyan
  "#a26dff", // violet
];

interface UploadedImage {
  id: string;
  name: string;
  url: string; // object URL
}

interface PrintCard {
  index: number; // 1-based
  label: string; // "01/10"
  imageUrl: string;
  caption: string;
  dTag: string;
  naddr: string;
  keyPair: VerificationKeyPair;
  claimUrl: string;
  qrDataUrl: string;
}

/** Split a flat list into fixed-size pages for the printed sheet. */
function chunkPages<T>(items: T[], perPage: number): T[][] {
  const pages: T[][] = [];
  for (let i = 0; i < items.length; i += perPage) {
    pages.push(items.slice(i, i + perPage));
  }
  return pages;
}

/**
 * Swap column order within each row of a 2-col grid so the printed back
 * aligns with the front under long-edge duplex flip.
 */
function swapColsForDuplex<T>(page: T[]): (T | undefined)[] {
  const out: (T | undefined)[] = [];
  for (let r = 0; r < page.length; r += 2) {
    out.push(page[r + 1]);
    out.push(page[r]);
  }
  return out;
}

/** Deterministic pseudo-random for stable confetti placement. */
function rand(seed: number, salt: number): number {
  const x = Math.sin(seed * 9973 + salt * 31) * 10000;
  return x - Math.floor(x);
}

export default function PrintGenerator() {
  const { user } = useCurrentUser();
  const [config] = useState<PrintConfig>(DEFAULT_CONFIG);
  const [caption, setCaption] = useState("");
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [cards, setCards] = useState<PrintCard[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [printMode, setPrintMode] = useState<"front" | "back" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const pubkey = user?.pubkey ?? "";
  const npub = user ? nip19.npubEncode(user.pubkey) : "";

  const handleFiles = useCallback((files: FileList | null) => {
    if (!files) return;
    const next: UploadedImage[] = [];
    for (const file of Array.from(files)) {
      if (!file.type.startsWith("image/")) continue;
      next.push({
        id: `${file.name}-${file.size}-${crypto.randomUUID()}`,
        name: file.name,
        url: URL.createObjectURL(file),
      });
    }
    setImages((prev) => [...prev, ...next]);
    setCards([]); // invalidate previously generated cards
  }, []);

  const removeImage = useCallback((id: string) => {
    setImages((prev) => {
      const found = prev.find((i) => i.id === id);
      if (found) URL.revokeObjectURL(found.url);
      return prev.filter((i) => i.id !== id);
    });
    setCards([]);
  }, []);

  const clearImages = useCallback(() => {
    setImages((prev) => {
      prev.forEach((i) => URL.revokeObjectURL(i.url));
      return [];
    });
    setCards([]);
  }, []);

  const generateCards = useCallback(async () => {
    setError(null);
    if (!pubkey) {
      setError("Log in with Nostr first — prints bind to your npub.");
      return;
    }
    if (images.length === 0) {
      setError("Upload at least one image first.");
      return;
    }

    setIsGenerating(true);
    try {
      // Pre-rasterize the chest logo PNG so every card back has a
      // synchronous source to overlay (survives the print state churn).
      await loadChestPng(600, config.recolorLogo);

      const total = images.length;
      const next: PrintCard[] = [];
      for (let i = 0; i < total; i++) {
        const image = images[i];
        const dTag = generateCompactDTag();
        const naddr = printToNaddr(pubkey, dTag, config.relays);
        const keyPair = await generateVerificationKeyPair();
        const claimUrl = buildClaimUrl(config.claimOrigin, naddr, keyPair.nsec);
        const qrDataUrl = await generateClaimQR(claimUrl, 900, config.qrColor);
        next.push({
          index: i + 1,
          label: `${String(i + 1).padStart(2, "0")}/${String(total).padStart(2, "0")}`,
          imageUrl: image.url,
          caption: caption.trim(),
          dTag,
          naddr,
          keyPair,
          claimUrl,
          qrDataUrl,
        });
      }
      setCards(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to generate cards");
    } finally {
      setIsGenerating(false);
    }
  }, [pubkey, images, caption, config]);

  const frontPages = useMemo(() => chunkPages(cards, 4), [cards]);
  const backPages = useMemo(
    () => chunkPages(cards, 4).map(swapColsForDuplex),
    [cards],
  );

  const print = (mode: "front" | "back") => {
    setPrintMode(mode);
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrintMode(null), 500);
    }, 100);
  };

  const buildExport = () =>
    cards.map((c) => ({
      index: c.index,
      label: c.label,
      image: images[c.index - 1]?.name ?? null,
      creatorPubkey: pubkey,
      creatorNpub: npub,
      dTag: c.dTag,
      naddr: c.naddr,
      nsec: c.keyPair.nsec,
      verificationPubkey: c.keyPair.publicKey,
      claimUrl: c.claimUrl,
    }));

  const handleCopyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(buildExport(), null, 2));
    } catch {
      setError("Could not access clipboard");
    }
  };

  const handleDownloadJson = () => {
    const blob = new Blob([JSON.stringify(buildExport(), null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `popart-treasures-${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  return (
    <>
      <PrintStyles />

      {/* ---------- SCREEN VIEW ---------- */}
      <div
        className="tt-screen-only relative min-h-screen w-full overflow-hidden"
        style={{ background: TROLL_PURPLE }}
      >
        <PopArtBackdrop />

        <div className="relative z-10 mx-auto max-w-6xl px-4 py-6">
          {/* Top bar */}
          <div className="mb-8 flex items-center justify-between gap-4">
            <span
              className="text-sm font-black uppercase tracking-[0.18em] text-white"
              style={{ textShadow: "0 2px 6px rgba(0,0,0,0.6)" }}
            >
              <span style={{ color: "#ffd23f" }}>PopArt</span>
              <span className="mx-1" style={{ color: "#3ec1ff" }}>
                Treasures
              </span>
            </span>
            <LoginArea />
          </div>

          {/* Headline */}
          <header className="mb-8 text-center">
            <h1
              className="mb-4 inline-block text-4xl font-black leading-[1.05] tracking-tight md:text-6xl"
              style={{
                backgroundImage:
                  "linear-gradient(90deg, #ffd23f 0%, #ff7a1a 22%, #ff3ea5 44%, #a26dff 64%, #3ec1ff 82%, #3ddc97 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
                filter: [
                  "drop-shadow(-3px -3px 0 #000)",
                  "drop-shadow(3px -3px 0 #000)",
                  "drop-shadow(-3px 3px 0 #000)",
                  "drop-shadow(3px 3px 0 #000)",
                  "drop-shadow(0 10px 20px rgba(0,0,0,0.5))",
                ].join(" "),
              }}
            >
              PopArt Treasures
            </h1>
            <p className="mx-auto max-w-2xl text-base font-semibold text-white [text-shadow:0_2px_6px_rgba(0,0,0,0.55)] md:text-lg">
              Upload your art, mint one-time claim keys per image, and print
              double-sided cards — art front, QR back — duplex on US Letter.
            </p>
          </header>

          <HowToCard />

          {!user && (
            <div className="mx-auto mb-8 max-w-md rounded-2xl border-4 border-black bg-white/95 p-6 text-center shadow-[8px_8px_0_0_#000]">
              <Sparkles className="mx-auto mb-2 h-8 w-8 text-[#ff3ea5]" />
              <p className="mb-4 font-bold text-[#3d1a5e]">
                Log in with Nostr to mint prints bound to your npub.
              </p>
              <div className="flex justify-center">
                <LoginArea />
              </div>
            </div>
          )}

          {user && (
            <>
              {/* Creator + caption */}
              <section className="mb-6 grid gap-4 rounded-2xl border-4 border-black bg-white/95 p-4 shadow-[6px_6px_0_0_#000] sm:grid-cols-2">
                <div className="flex flex-col gap-1 text-sm">
                  <span className="font-black uppercase tracking-wide text-[#3d1a5e]">
                    Binding to npub
                  </span>
                  <span className="truncate rounded-lg border-2 border-black bg-[#fff6da] px-3 py-2 font-mono text-xs">
                    {npub}
                  </span>
                </div>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="font-black uppercase tracking-wide text-[#3d1a5e]">
                    Card caption (optional)
                  </span>
                  <input
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder="Shown under the QR on each back"
                    className="rounded-lg border-2 border-black px-3 py-2 text-sm"
                  />
                </label>
              </section>

              {/* Upload zone */}
              <section className="mb-6">
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    handleFiles(e.dataTransfer.files);
                  }}
                  className="flex flex-col items-center justify-center gap-2 rounded-2xl border-4 border-dashed border-black bg-white/90 p-8 text-center shadow-[6px_6px_0_0_#000]"
                >
                  <Upload className="h-8 w-8 text-[#a26dff]" />
                  <p className="text-sm font-semibold text-[#3d1a5e]">
                    Drag & drop art here, or
                  </p>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="rounded-xl border-2 border-black px-5 py-2.5 text-sm font-black uppercase tracking-wide text-white shadow-[3px_3px_0_0_#000] transition-transform hover:scale-105 active:scale-95"
                    style={{
                      background:
                        "linear-gradient(135deg, #ff7a1a 0%, #ff3ea5 100%)",
                    }}
                  >
                    Choose images
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => handleFiles(e.target.files)}
                  />
                  <p className="text-xs text-gray-500">
                    One print is created per image, in upload order.
                  </p>
                </div>

                {images.length > 0 && (
                  <div className="mt-4 rounded-2xl border-4 border-black bg-white/90 p-4 shadow-[6px_6px_0_0_#000]">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-sm font-black uppercase text-[#3d1a5e]">
                        {images.length} image{images.length === 1 ? "" : "s"}
                      </span>
                      <button
                        onClick={clearImages}
                        className="flex items-center gap-1 text-xs font-bold text-red-600 hover:underline"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Clear all
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-3 sm:grid-cols-5 lg:grid-cols-8">
                      {images.map((img) => (
                        <div
                          key={img.id}
                          className="group relative aspect-square overflow-hidden rounded-lg border-2 border-black bg-gray-50"
                        >
                          <img
                            src={img.url}
                            alt={img.name}
                            className="h-full w-full object-cover"
                          />
                          <button
                            onClick={() => removeImage(img.id)}
                            className="absolute right-1 top-1 hidden rounded-full border-2 border-black bg-[#ff3ea5] p-1 text-white group-hover:block"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>

              {/* Actions */}
              <section className="mb-6 flex flex-wrap items-center gap-3">
                <button
                  onClick={generateCards}
                  disabled={isGenerating || images.length === 0}
                  className="flex items-center gap-1.5 rounded-xl border-2 border-black px-5 py-2.5 text-sm font-black uppercase tracking-wide text-white shadow-[3px_3px_0_0_#000] transition-transform hover:scale-105 active:scale-95 disabled:opacity-40 disabled:hover:scale-100"
                  style={{
                    background:
                      "linear-gradient(135deg, #3ddc97 0%, #3ec1ff 100%)",
                  }}
                >
                  {isGenerating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ImageIcon className="h-4 w-4" />
                  )}
                  Generate {images.length || ""} print
                  {images.length === 1 ? "" : "s"}
                </button>
                <PopButton
                  onClick={handleCopyJson}
                  disabled={!cards.length}
                  color="#ffd23f"
                >
                  <Copy className="h-4 w-4" /> Copy JSON
                </PopButton>
                <PopButton
                  onClick={handleDownloadJson}
                  disabled={!cards.length}
                  color="#a26dff"
                  light
                >
                  <Download className="h-4 w-4" /> Download JSON
                </PopButton>
                <PopButton
                  onClick={() => print("front")}
                  disabled={!cards.length}
                  color="#ff7a1a"
                  light
                >
                  <Printer className="h-4 w-4" /> Print Fronts
                </PopButton>
                <PopButton
                  onClick={() => print("back")}
                  disabled={!cards.length}
                  color="#ff3ea5"
                  light
                >
                  <Printer className="h-4 w-4" /> Print Backs
                </PopButton>
              </section>
            </>
          )}

          {error && (
            <p className="mb-6 rounded-xl border-2 border-black bg-[#ffe1ec] px-3 py-2 text-sm font-semibold text-red-700">
              {error}
            </p>
          )}

          {/* Preview */}
          {cards.length > 0 && (
            <>
              <section className="mb-10">
                <h2 className="mb-3 text-xl font-black uppercase tracking-wide text-white [text-shadow:0_2px_6px_rgba(0,0,0,0.55)]">
                  Fronts (Art)
                </h2>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-5">
                  {cards.map((card) => (
                    <CardFront key={`front-${card.index}`} card={card} preview />
                  ))}
                </div>
              </section>

              <section className="pb-12">
                <h2 className="mb-3 text-xl font-black uppercase tracking-wide text-white [text-shadow:0_2px_6px_rgba(0,0,0,0.55)]">
                  Backs (QR + label)
                </h2>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-5">
                  {cards.map((card) => (
                    <CardBack
                      key={`back-${card.index}`}
                      card={card}
                      config={config}
                      preview
                    />
                  ))}
                </div>
              </section>
            </>
          )}
        </div>
      </div>

      {/* ---------- PRINT VIEW ---------- */}
      <div className="tt-print-root tt-print-only">
        {printMode === "front" &&
          frontPages.map((page, idx) => (
            <div className="tt-print-page" key={`front-page-${idx}`}>
              {page.map((card) => (
                <CardFront key={`p-front-${card.index}`} card={card} />
              ))}
              {Array.from({ length: 4 - page.length }).map((_, i) => (
                <div key={`empty-${i}`} className="tt-card" />
              ))}
            </div>
          ))}

        {printMode === "back" &&
          backPages.map((page, idx) => (
            <div
              className="tt-print-page"
              key={`back-page-${idx}`}
              style={{
                transform: `translate(${BACK_OFFSET_X}in, ${BACK_OFFSET_Y}in)`,
              }}
            >
              {page.map((card, i) =>
                card ? (
                  <CardBack
                    key={`p-back-${card.index}-${i}`}
                    card={card}
                    config={config}
                  />
                ) : (
                  <div key={`back-empty-${idx}-${i}`} className="tt-card" />
                ),
              )}
              {Array.from({ length: 4 - page.length }).map((_, i) => (
                <div key={`empty-back-${i}`} className="tt-card" />
              ))}
            </div>
          ))}
      </div>
    </>
  );
}

/**
 * Brief end-user instructions for turning a printed PopArt Treasures
 * card into a live treasure in the Treasures app — including the
 * "First to Find" and "Art" treasure modifiers (NIP-GC `n` tags) set on
 * the Details step alongside the cache type.
 */
function HowToCard() {
  const steps: Array<{
    icon: React.ReactNode;
    title: string;
    body: React.ReactNode;
  }> = [
    {
      icon: <QrCode className="h-5 w-5" />,
      title: "1. Scan the card",
      body: (
        <>
          Point your phone camera at the QR on the back of a card (or open{" "}
          <span className="font-mono">treasures.to/claim</span> and paste the
          claim URL). Tap to open it in Treasures.
        </>
      ),
    },
    {
      icon: <Sparkles className="h-5 w-5" />,
      title: "2. Log in & claim",
      body: (
        <>
          Sign in to Treasures with the same npub the card was generated for,
          then choose <strong>Fill out listing now</strong> to start the
          listing wizard.
        </>
      ),
    },
    {
      icon: <MapPin className="h-5 w-5" />,
      title: "3. Location",
      body: (
        <>
          Drop a pin where you hid the art and wait for the{" "}
          <strong>Location looks good</strong> confirmation.
        </>
      ),
    },
    {
      icon: <Trophy className="h-5 w-5" />,
      title: "4. Details & modifiers",
      body: (
        <>
          Add a <strong>Name</strong> and <strong>Description</strong>, pick the{" "}
          <strong>Type</strong>, then expand <strong>Advanced</strong> and
          toggle <strong>First to Find</strong> (single exclusive claim) and{" "}
          <strong>Art</strong> (the treasure is a work of art).
        </>
      ),
    },
    {
      icon: <ImageIcon className="h-5 w-5" />,
      title: "5. Challenge & photo",
      body: (
        <>
          Set <strong>Difficulty</strong> / <strong>Terrain</strong>, then on{" "}
          <strong>Finish</strong> add a photo of the print art via{" "}
          <strong>Camera</strong> or <strong>Library</strong>.
        </>
      ),
    },
    {
      icon: <Printer className="h-5 w-5" />,
      title: "6. Publish",
      body: (
        <>
          Tap <strong>Create</strong> — your PopArt treasure is now live for
          finders.
        </>
      ),
    },
  ];

  return (
    <section className="mx-auto mb-8 max-w-3xl rounded-2xl border-4 border-black bg-white/95 p-5 shadow-[8px_8px_0_0_#000]">
      <h2 className="mb-1 text-lg font-black uppercase tracking-wide text-[#3d1a5e]">
        How to create a PopArt treasure
      </h2>
      <p className="mb-4 text-sm text-gray-600">
        Once a card is printed and hidden, here's how you turn it into a live
        treasure in the Treasures app.
      </p>
      <ol className="grid gap-3 sm:grid-cols-2">
        {steps.map((s, i) => (
          <li
            key={i}
            className="flex gap-3 rounded-xl border-2 border-black bg-[#fff6da] p-3"
          >
            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg border-2 border-black bg-[#ffd23f] text-[#3d1a5e]">
              {s.icon}
            </span>
            <span className="text-sm leading-snug text-[#3d1a5e]">
              <span className="block font-black uppercase tracking-wide">
                {s.title}
              </span>
              <span className="font-medium">{s.body}</span>
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** Pop-art secondary action button (white card with colored shadow). */
function PopButton({
  onClick,
  disabled,
  color,
  light,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  color: string;
  light?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex items-center gap-1.5 rounded-xl border-2 border-black px-4 py-2.5 text-sm font-black uppercase tracking-wide shadow-[3px_3px_0_0_#000] transition-transform hover:scale-105 active:scale-95 disabled:opacity-40 disabled:hover:scale-100"
      style={{ background: color, color: light ? "#fff" : "#3d1a5e" }}
    >
      {children}
    </button>
  );
}

/**
 * Front of a card — the uploaded art, 4x4in. Explicit physical
 * dimensions tell the print pipeline the rasterization target, which
 * keeps printed output crisp.
 */
function CardFront({ card, preview }: { card: PrintCard; preview?: boolean }) {
  return (
    <div
      className={cn(
        "tt-card tt-card-inner relative overflow-hidden",
        preview && "rounded-lg border-4 border-black shadow-[6px_6px_0_0_#000]",
      )}
      style={{
        background: "#111",
        width: preview ? undefined : "4in",
        height: preview ? undefined : "4in",
        aspectRatio: preview ? "1 / 1" : undefined,
        WebkitPrintColorAdjust: "exact",
        printColorAdjust: "exact",
      }}
    >
      <img
        src={card.imageUrl}
        alt={`Print ${card.label}`}
        className={preview ? "absolute inset-0 h-full w-full object-cover" : "absolute"}
        style={
          preview
            ? undefined
            : {
                top: 0,
                left: 0,
                width: "4in",
                height: "4in",
                objectFit: "cover",
                imageRendering: "auto",
              }
        }
      />
    </div>
  );
}

/**
 * Back of a card — branded claim QR with the print number above and a
 * configurable caption + heading below. Sized with margin so the QR
 * never bleeds off the trimmed edge.
 */
function CardBack({
  card,
  config,
  preview,
}: {
  card: PrintCard;
  config: PrintConfig;
  preview?: boolean;
}) {
  return (
    <div
      className={cn(
        "tt-card tt-card-inner relative flex flex-col items-center justify-center overflow-hidden bg-white",
        preview && "rounded-lg border-4 border-black shadow-[6px_6px_0_0_#000]",
      )}
      style={{
        width: preview ? undefined : "4in",
        height: preview ? undefined : "4in",
        aspectRatio: preview ? "1 / 1" : undefined,
        padding: preview ? "0.4rem" : "0.22in",
        WebkitPrintColorAdjust: "exact",
        printColorAdjust: "exact",
      }}
    >
      <div
        className="text-center font-black uppercase tracking-widest"
        style={{
          color: config.textColor,
          fontSize: preview ? "0.75rem" : "0.14in",
          marginBottom: preview ? "0.15rem" : "0.04in",
        }}
      >
        {card.label}
      </div>

      {card.caption && (
        <div
          className="text-center italic"
          style={{
            color: config.textColor,
            fontSize: preview ? "0.75rem" : "0.14in",
            fontWeight: 600,
            lineHeight: 1.25,
            maxWidth: preview ? "92%" : "3.4in",
            marginBottom: preview ? "0.3rem" : "0.08in",
          }}
        >
          {card.caption}
        </div>
      )}

      <div
        className="relative"
        style={{
          width: preview ? "72%" : "2.8in",
          height: preview ? undefined : "2.8in",
          aspectRatio: preview ? "1 / 1" : undefined,
          flexShrink: 0,
        }}
      >
        <img
          src={card.qrDataUrl}
          alt="claim QR code"
          className="block h-full w-full"
        />
        {/* Chest logo overlay — ~32% of QR width, centered over the
            white well punched into the QR. Rendered as a rasterized PNG
            so it survives the print state churn. */}
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          aria-hidden
        >
          <div style={{ width: "32%", height: "32%" }}>
            <ChestLogo recolor={config.recolorLogo} />
          </div>
        </div>
      </div>

      <div
        className="w-full text-center"
        style={{ marginTop: preview ? "0.3rem" : "0.08in" }}
      >
        <div
          className="font-black uppercase tracking-widest"
          style={{
            color: config.textColor,
            fontSize: preview ? "0.65rem" : "0.12in",
          }}
        >
          {config.backHeading}
        </div>
      </div>
    </div>
  );
}

/**
 * Recolored treasures chest logo for the QR center. Reads the
 * pre-rasterized PNG out of module cache so it renders synchronously
 * (critical for the print flow). Returns null until the cache is primed
 * by `generateCards`.
 */
function ChestLogo({ recolor }: { recolor: boolean }) {
  const png = getCachedChestPng(recolor);
  if (!png) return null;
  return (
    <img
      src={png}
      alt=""
      aria-hidden="true"
      className="block h-full w-full"
      style={{ objectFit: "contain" }}
    />
  );
}

/**
 * Floating pop-art confetti — colored circles, diamonds & sparkles
 * drifting up the page. Ported from the Treasure Trolls teaser.
 */
function PopConfetti({ count = 28 }: { count?: number }) {
  const pieces = Array.from({ length: count }, (_, i) => {
    const color = POP_COLORS[i % POP_COLORS.length];
    const left = rand(i, 1) * 100;
    const size = 8 + rand(i, 2) * 18;
    const duration = 14 + rand(i, 3) * 12;
    const delay = -rand(i, 4) * 20;
    const drift = (rand(i, 5) - 0.5) * 30;
    const rotate = rand(i, 6) * 720;
    const shape = rand(i, 7);
    const startTop = 100 + rand(i, 8) * 20;
    return { color, left, size, duration, delay, drift, rotate, shape, startTop, i };
  });

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-[2] overflow-hidden"
    >
      {pieces.map((p) => {
        let inner: React.ReactNode;
        if (p.shape < 0.4) {
          inner = (
            <span
              style={{
                width: p.size,
                height: p.size,
                background: p.color,
                borderRadius: "50%",
                boxShadow: `0 0 ${p.size * 0.6}px ${p.color}aa`,
                display: "block",
              }}
            />
          );
        } else if (p.shape < 0.65) {
          inner = (
            <span
              style={{
                width: p.size,
                height: p.size,
                background: p.color,
                borderRadius: 4,
                boxShadow: `0 0 ${p.size * 0.5}px ${p.color}aa`,
                display: "block",
                transform: "rotate(45deg)",
              }}
            />
          );
        } else {
          inner = (
            <span
              style={{
                width: p.size,
                height: p.size,
                background: p.color,
                clipPath:
                  "polygon(50% 0%, 60% 40%, 100% 50%, 60% 60%, 50% 100%, 40% 60%, 0% 50%, 40% 40%)",
                filter: `drop-shadow(0 0 ${p.size * 0.4}px ${p.color})`,
                display: "block",
              }}
            />
          );
        }
        return (
          <span
            key={p.i}
            style={{
              position: "absolute",
              top: `${p.startTop}%`,
              left: `${p.left}%`,
              animation: `ap-confetti-float ${p.duration}s linear ${p.delay}s infinite`,
              ["--drift" as never]: `${p.drift}vw`,
              ["--end-rot" as never]: `${p.rotate}deg`,
              willChange: "transform, opacity",
            }}
          >
            {inner}
          </span>
        );
      })}
    </div>
  );
}

/** Aurora blobs + dotted trail lines + confetti behind the UI. */
function PopArtBackdrop() {
  return (
    <>
      {/* Aurora blobs */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
      >
        <div
          className="absolute -left-32 -top-32 h-[55vw] w-[55vw] rounded-full opacity-60 blur-3xl"
          style={{
            background:
              "radial-gradient(circle, #ff3ea5 0%, rgba(255,62,165,0) 70%)",
            animation: "ap-aurora-shift 18s ease-in-out infinite",
          }}
        />
        <div
          className="absolute -right-32 -top-24 h-[60vw] w-[60vw] rounded-full opacity-50 blur-3xl"
          style={{
            background:
              "radial-gradient(circle, #3ec1ff 0%, rgba(62,193,255,0) 70%)",
            animation: "ap-aurora-shift 22s ease-in-out infinite reverse",
          }}
        />
        <div
          className="absolute left-1/4 top-1/3 h-[45vw] w-[45vw] rounded-full opacity-45 blur-3xl"
          style={{
            background:
              "radial-gradient(circle, #ffd23f 0%, rgba(255,210,63,0) 70%)",
            animation: "ap-aurora-shift 24s ease-in-out infinite",
          }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 h-[50vw] w-[50vw] rounded-full opacity-50 blur-3xl"
          style={{
            background:
              "radial-gradient(circle, #3ddc97 0%, rgba(61,220,151,0) 70%)",
            animation: "ap-aurora-shift 20s ease-in-out infinite reverse",
          }}
        />
      </div>

      {/* Dotted treasure-trail lines */}
      <svg
        className="pointer-events-none absolute inset-0 z-[1] h-full w-full text-white opacity-[0.16]"
        viewBox="0 0 200 100"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        <path
          d="M -5,20 C 15,10 30,12 50,22 C 70,32 85,8 110,16 C 135,24 155,12 180,20 C 195,24 200,18 210,20"
          stroke="currentColor"
          strokeWidth="1.2"
          fill="none"
          strokeDasharray="4,3"
          strokeLinecap="round"
        />
        <path
          d="M -5,80 C 20,70 40,72 60,82 C 80,92 95,66 120,74 C 145,82 160,70 180,78 C 195,84 200,76 210,80"
          stroke="currentColor"
          strokeWidth="1.2"
          fill="none"
          strokeDasharray="4,3"
          strokeLinecap="round"
        />
      </svg>

      <PopConfetti count={30} />
    </>
  );
}

/**
 * Print-specific styles + pop-art keyframes. During @media print we
 * collapse everything to visibility:hidden, then re-reveal only the
 * print root subtree, so the app shell never leaks into the printed
 * sheet. Each page is US Letter (8.5x11") with 4-up 4x4" cards.
 */
function PrintStyles() {
  return (
    <style>{`
      .tt-card { width: 4in; height: 4in; }

      @keyframes ap-aurora-shift {
        0%   { transform: translate3d(0, 0, 0) rotate(0deg); }
        50%  { transform: translate3d(2%, -3%, 0) rotate(2deg); }
        100% { transform: translate3d(0, 0, 0) rotate(0deg); }
      }
      @keyframes ap-confetti-float {
        0%   { transform: translate3d(0, 0, 0) rotate(0deg); opacity: 0; }
        10%  { opacity: 1; }
        90%  { opacity: 1; }
        100% { transform: translate3d(var(--drift), -130vh, 0) rotate(var(--end-rot)); opacity: 0; }
      }

      @media screen {
        .tt-print-only { display: none; }
      }

      @media print {
        @page { size: letter portrait; margin: 0.25in; }
        html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }

        .tt-print-only { display: block !important; }

        body * { visibility: hidden !important; }

        .tt-print-root,
        .tt-print-root * { visibility: visible !important; }

        .tt-print-root {
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
          background: #fff !important;
        }

        .tt-screen-only { display: none !important; }
        .tt-card-inner { box-shadow: none !important; border: none !important; }
      }

      .tt-print-page {
        width: 8in;
        height: 10.5in;
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 1fr 1fr;
        gap: 0.05in;
        page-break-after: always;
        break-after: page;
        justify-items: center;
        align-items: center;
      }
      .tt-print-page:last-child { page-break-after: auto; break-after: auto; }
    `}</style>
  );
}
